import { useTranslation } from 'react-i18next';
import type { GoalType } from '@flugwettrennen/shared';

/** Die nachträglich änderbaren Regel-Optionen einer Runde, als Formularwerte. */
export interface RoundOptionValues {
  /** Eigenes X je Top-Ziel – kombiniert hieße sonst "Top 10 Inseln" auch "Top 10 Airlines". */
  topXIslands: number;
  topXAirlines: number;
  forbiddenTopXAirports: number;
  uniqueAirportsOnly: boolean;
  failProbability: number;
  minLayoverMinutes: number;
  changeCutoffMinutes: number;
  customGoalText: string;
}

export const DEFAULT_OPTION_VALUES: RoundOptionValues = {
  topXIslands: 5,
  topXAirlines: 5,
  forbiddenTopXAirports: 0,
  uniqueAirportsOnly: false,
  failProbability: 2 / 3,
  minLayoverMinutes: 40,
  changeCutoffMinutes: 30,
  customGoalText: '',
};

interface Props {
  values: RoundOptionValues;
  onChange: (patch: Partial<RoundOptionValues>) => void;
  goalTypes: GoalType[];
  /** Top X steckt in der Ziel-Checkliste und ist nach dem ersten Flug fest. */
  topXLocked?: boolean;
}

/**
 * Die Regel-Optionen einer Runde. Wird beim Anlegen und beim nachträglichen
 * Bearbeiten genutzt, damit beide Formulare nicht auseinanderlaufen.
 */
export function RoundOptionsFields({ values, onChange, goalTypes, topXLocked = false }: Props) {
  const { t } = useTranslation();
  const isFree = goalTypes.includes('free');

  /** Ein X-Feld je gewähltem Top-Ziel. */
  const topXField = (id: string, label: string, key: 'topXIslands' | 'topXAirlines') => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        min={1}
        max={50}
        value={values[key]}
        disabled={topXLocked}
        onChange={(e) => onChange({ [key]: Number(e.target.value) })}
      />
      {topXLocked && <span className="hint">{t('settings.topXLocked')}</span>}
    </div>
  );

  return (
    <>
      {goalTypes.includes('top_islands') &&
        topXField('tx-islands', t('create.topXIslands'), 'topXIslands')}
      {goalTypes.includes('top_airlines') &&
        topXField('tx-airlines', t('create.topXAirlines'), 'topXAirlines')}

      {isFree && (
        <div className="field">
          <label htmlFor="cg">{t('create.customGoal')}</label>
          <textarea
            id="cg"
            rows={2}
            value={values.customGoalText}
            onChange={(e) => onChange({ customGoalText: e.target.value })}
          />
        </div>
      )}

      <div className="field">
        <label htmlFor="ftx">{t('create.forbiddenTopX')}</label>
        <input
          id="ftx"
          type="number"
          min={0}
          max={100}
          value={values.forbiddenTopXAirports}
          onChange={(e) => onChange({ forbiddenTopXAirports: Number(e.target.value) })}
        />
      </div>

      <label className="row" style={{ gap: 8 }}>
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          checked={values.uniqueAirportsOnly}
          onChange={(e) => onChange({ uniqueAirportsOnly: e.target.checked })}
        />
        <span>{t('create.uniqueAirports')}</span>
      </label>

      <div className="field">
        <label htmlFor="fp">
          {t('create.failProbability')}: <b>{Math.round(values.failProbability * 100)}%</b>
        </label>
        <input
          id="fp"
          type="range"
          min={0}
          max={0.9}
          step={0.05}
          value={values.failProbability}
          onChange={(e) => onChange({ failProbability: Number(e.target.value) })}
        />
      </div>

      <div className="row" style={{ gap: 12 }}>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="ml">{t('create.minLayover')}</label>
          <input
            id="ml"
            type="number"
            min={0}
            max={600}
            value={values.minLayoverMinutes}
            onChange={(e) => onChange({ minLayoverMinutes: Number(e.target.value) })}
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="cc">{t('create.changeCutoff')}</label>
          <input
            id="cc"
            type="number"
            min={0}
            max={600}
            value={values.changeCutoffMinutes}
            onChange={(e) => onChange({ changeCutoffMinutes: Number(e.target.value) })}
          />
        </div>
      </div>
    </>
  );
}
